/**
 * Модель кабинета хозяйства и карточки контура (agroai-6al.34): чистые
 * функции над ответами API. Чисел здесь не придумывается — только раскладка
 * готовых серверных величин по блокам и факты для правил приоритета
 * (`priority.ts`).
 */

import type { Schemas } from '../../../api/client';
import { defineMessages } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';
import { cropLabel, cropName } from '../../../lib/crops';
import { messageRecord } from '../../lib/messageRecord';
import type { ValuesLayer, WaterStateValue } from '../../map/layers';
import type { QuarantineStatusValue } from '../../data/types';
import { waterByAllocations } from '../../map/layers';
import type { ContourFacts, FarmFacts } from './priority';

export type SeasonCycle = Schemas['SeasonCycleOut'];
export type CampaignWork = Schemas['SeasonCampaignWorkOut'];
export type FarmBreakdown = Schemas['FarmBreakdownOut'];
export type FarmLand = Schemas['FarmLandOut'];
export type FarmContourRow = Schemas['FarmContourRowOut'];
export type FarmQuarantine = Schemas['FarmQuarantineOut'];
export type ContourHistory = Schemas['ContourHistoryOut'];
export type ContourWork = Schemas['ContourWorkOut'];

const m = defineMessages({
  phaseDone: { id: 'farm.model.phase.done', defaultMessage: 'выполнено', description: 'Состояние работы агроцикла хозяйства: все работы на полях выполнены; бейдж/фильтр, строчными' },
  phaseInProgress: { id: 'farm.model.phase.inProgress', defaultMessage: 'в процессе', description: 'Состояние работы агроцикла хозяйства: отчёт есть, работа не закончена; бейдж/фильтр, строчными' },
  phaseOverdue: { id: 'farm.model.phase.overdue', defaultMessage: 'после срока', description: 'Состояние работы агроцикла хозяйства: окно закрыто, отчёта нет; бейдж/фильтр, строчными' },
  phaseInWindow: { id: 'farm.model.phase.inWindow', defaultMessage: 'окно идёт', description: 'Состояние работы агроцикла хозяйства: срок выполнения идёт, отчёта пока нет (не просрочка); бейдж/фильтр' },
  phaseAhead: { id: 'farm.model.phase.ahead', defaultMessage: 'впереди', description: 'Состояние работы агроцикла хозяйства: окно ещё не открылось; бейдж/фильтр' },
  reqOpen: { id: 'farm.model.req.open', defaultMessage: 'открыто', description: 'Состояние предписания карантина: срок не вышел, не устранено; бейдж' },
  reqOverdue: { id: 'farm.model.req.overdue', defaultMessage: 'после срока', description: 'Состояние предписания карантина: срок вышел, не устранено; бейдж' },
  reqExtended: { id: 'farm.model.req.extended', defaultMessage: 'срок продлён', description: 'Состояние предписания карантина: срок продлён новым предписанием-продолжением; открытым считается продолжение, это — нет; бейдж, строчными' },
  reqEliminated: { id: 'farm.model.req.eliminated', defaultMessage: 'устранено', description: 'Состояние предписания карантина: устранено по замеру инспектора; бейдж' },
  reqEliminatedLate: { id: 'farm.model.req.eliminatedLate', defaultMessage: 'устранено после срока', description: 'Состояние предписания карантина: устранено, но после срока; бейдж' },
  qGreen: { id: 'farm.model.qStatus.green', defaultMessage: 'зелёный', description: 'Карантинный статус контура/хозяйства: нет находок (цвет); метка, строчными' },
  qYellow: { id: 'farm.model.qStatus.yellow', defaultMessage: 'жёлтый', description: 'Карантинный статус контура/хозяйства: под наблюдением (цвет); метка, строчными' },
  qRed: { id: 'farm.model.qStatus.red', defaultMessage: 'красный', description: 'Карантинный статус контура/хозяйства: есть находка, требуется устранение (цвет); метка, строчными' },
  dirPests: { id: 'farm.model.direction.pests', defaultMessage: 'вредители', description: 'Направление карантина (вид находки): вредители; строчными, перед названием проблемы' },
  dirDiseases: { id: 'farm.model.direction.diseases', defaultMessage: 'болезни', description: 'Направление карантина (вид находки): болезни; строчными, перед названием проблемы' },
  dirWeeds: { id: 'farm.model.direction.weeds', defaultMessage: 'сорняки', description: 'Направление карантина (вид находки): сорняки; строчными, перед названием проблемы' },
  fuEliminated: { id: 'farm.model.followup.eliminated', defaultMessage: 'устранена', description: 'Итог повторного контроля находки: проблема устранена (женский род, о проблеме)' },
  fuReduced: { id: 'farm.model.followup.reduced', defaultMessage: 'уменьшилась', description: 'Итог повторного контроля находки: проблема уменьшилась (женский род)' },
  fuUnchanged: { id: 'farm.model.followup.unchanged', defaultMessage: 'сохранилась', description: 'Итог повторного контроля находки: проблема сохранилась (женский род)' },
  fuIncreased: { id: 'farm.model.followup.increased', defaultMessage: 'увеличилась', description: 'Итог повторного контроля находки: проблема увеличилась (женский род)' },
  cwReported: { id: 'farm.model.contourWork.reported', defaultMessage: 'выполнено', description: 'Статус работы на контуре: отчёт «выполнено»; строчными' },
  cwInProgress: { id: 'farm.model.contourWork.inProgress', defaultMessage: 'в процессе', description: 'Статус работы на контуре: работа идёт; строчными' },
  cwOverdue: { id: 'farm.model.contourWork.overdue', defaultMessage: 'после срока', description: 'Статус работы на контуре: окно закрыто, отчёта нет; строчными' },
  cwInWindow: { id: 'farm.model.contourWork.inWindow', defaultMessage: 'окно идёт', description: 'Статус работы на контуре: срок идёт, отчёта пока нет; строчными' },
  cwAhead: { id: 'farm.model.contourWork.ahead', defaultMessage: 'впереди', description: 'Статус работы на контуре: окно ещё не открылось; строчными' },
  noCropName: { id: 'farm.model.direction.noCropName', defaultMessage: 'культура не названа', description: 'Подпись направления, у которого нет названия культуры; строчными' },
  seasonLayerTitle: { id: 'farm.model.cropLayer.title', defaultMessage: 'Культуры {year}', description: 'Название слоя карты хозяйства с культурами прошлого года агроцикла; {year} — год, число' },
  seasonLayerNone: { id: 'farm.model.cropLayer.none', defaultMessage: 'культура года не названа', description: 'Подпись контуров на карте хозяйства, у которых в выбранном году нет культуры; строчными' },
  outsideSeason: { id: 'farm.model.outsideSeason', defaultMessage: 'Вне агроцикла · {season}', description: 'Заголовок группы работ контура, не относящихся ни к одному агроциклу; {season} — сезон, например «2026»' },
  outside: { id: 'farm.model.outside', defaultMessage: 'Вне агроцикла', description: 'Заголовок группы работ контура, не относящихся ни к одному агроциклу и без указанного сезона' },
});

export interface MetricValue {
  id: string;
  value: number | null;
  unit: string;
  label: string;
  definition?: string | null;
  demo?: boolean | null;
  error?: string | null;
}

/** Метрики слоя (`GET /ai/metrics`) для кабинета: те же определения, что у района. */
export const FARM_METRIC_IDS = [
  // Ход работ по площади (решение №645) — главные числа кабинета; счётные —
  // вторым слоем «по числу работ».
  'area.done_pct',
  'area.done_ha',
  'area.work_ha',
  'area.in_progress_pct',
  'area.overdue_work_ha',
  // После срока — ЗЕМЛЯ хозяйства (каждый гектар один раз, журнал №690).
  'area.overdue_ha',
  'area.overdue_land_pct',
  'area.overdue_pct_closed',
  'reports.done_pct',
  'reports.done',
  'reports.in_progress',
  'reports.all_pct',
  'obligations.total',
  'overdue.count',
  'overdue.pct_closed',
  'harvest.plan_pct',
  'harvest.plan_t',
  'harvest.delivered_t',
  'harvest.yield_to_date_c_ha',
  'water.share_pct',
  'water.requested_th_m3',
  'water.delivered_th_m3',
  // Вода по файлу ирригации (agroai-0e8): основная ветка, заявки — запасная.
  'water.delivered_m3',
  'water.irrigated_land_ha',
  'water.irrigation_done_land_pct',
  'water.no_delivery_pairs',
  'quarantine.flagged_contours',
  'quarantine.red_contours',
  'quarantine.requirements_open',
  'quarantine.requirements_overdue',
] as const;

export function metricMap(rows: ReadonlyArray<MetricValue> | null | undefined): Record<string, MetricValue> {
  const out: Record<string, MetricValue> = {};
  for (const row of rows ?? []) out[row.id] = row;
  return out;
}

export function metricNumber(m: Record<string, MetricValue>, id: string): number | null {
  const v = m[id]?.value;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/* ---------------------------------------------------------------- циклы */

/** «Хлопок 2026», «Пшеница 2026-2027». */
export function cycleName(c: Pick<SeasonCycle, 'crop_code' | 'season_label'>): string {
  const crop = cropLabel(c.crop_code);
  return `${crop.known ? crop.label : c.crop_code} ${c.season_label}`;
}

/**
 * Основной цикл экрана: из адреса, если он среди циклов хозяйства; иначе
 * идущий с наибольшим числом обязательств; иначе последний по сезону.
 */
export function primaryCycle(items: ReadonlyArray<SeasonCycle>, fromUrl: string | null): SeasonCycle | null {
  if (fromUrl) {
    const hit = items.find((c) => c.cycle === fromUrl);
    if (hit) return hit;
  }
  const active = items.filter((c) => c.status === 'active').sort((a, b) => b.obligations.total - a.obligations.total);
  if (active[0]) return active[0];
  return [...items].sort((a, b) => b.season_label.localeCompare(a.season_label))[0] ?? null;
}

export type WorkPhase = 'done' | 'in_progress' | 'overdue' | 'in_window' | 'ahead';

export const WORK_PHASE_LABEL: Record<WorkPhase, string> = messageRecord<WorkPhase>({
  done: m.phaseDone,
  in_progress: m.phaseInProgress,
  overdue: m.phaseOverdue,
  in_window: m.phaseInWindow,
  ahead: m.phaseAhead,
});

/** Порядок фильтра работ: от требующего внимания к сделанному. */
export const WORK_PHASE_ORDER: WorkPhase[] = ['overdue', 'in_window', 'in_progress', 'done', 'ahead'];

/**
 * Выполнено / в процессе / нет отчёта одного счёта обязательств (журнал
 * решений №290): «выполнено» = отчёт «выполнено», «в процессе» — отчёт есть,
 * работа не закончена, «нет отчёта» — остаток. Сервер не разделил (старый
 * ответ без `done`) — `done = null`, а не «все отчитавшиеся выполнили».
 */
export function doneSplit(o: { total: number; reported: number; done?: number | null; in_progress?: number | null }): {
  total: number;
  reported: number;
  done: number | null;
  inProgress: number | null;
  silent: number;
} {
  const inProgress = o.in_progress ?? null;
  const done = o.done ?? (inProgress !== null ? o.reported - inProgress : null);
  return { total: o.total, reported: o.reported, done, inProgress, silent: Math.max(0, o.total - o.reported) };
}

/**
 * Состояние работы цикла хозяйства на дату: все обязательства с отчётом
 * «выполнено» — «выполнено»; окно ещё не открылось — «впереди»; окно
 * закрыто и есть «после срока» — «после срока»; отчёты есть у всех, но часть
 * «в процессе» — «в процессе»; иначе — «окно идёт». «Нет отчёта» при идущем
 * окне — не просрочка.
 */
export function workPhase(w: CampaignWork, asOf: string): WorkPhase {
  const o = doneSplit(w.totals.obligations);
  if (o.total > 0 && (o.done ?? o.reported) >= o.total) return 'done';
  if (w.window_from && w.window_from > asOf) return 'ahead';
  if ((w.totals.overdue.value ?? 0) > 0 || (w.window_to !== null && w.window_to < asOf && o.reported < o.total)) return 'overdue';
  if (o.total > 0 && o.reported >= o.total) return 'in_progress';
  return 'in_window';
}

/** Работы, чьё окно включает дату среза, — «что делается сейчас». */
export function currentWorks(works: ReadonlyArray<CampaignWork>, asOf: string): CampaignWork[] {
  return works
    .filter((w) => (!w.window_from || w.window_from <= asOf) && (!w.window_to || w.window_to >= asOf))
    .sort((a, b) => (a.window_to ?? '9999').localeCompare(b.window_to ?? '9999'));
}

/** Адрес работы цикла, суженной до хозяйства: контекст хозяйства едет с переходом. */
export function workHref(
  cycle: string,
  campaignId: number,
  farmId: number,
  linkSearch: (extra?: Record<string, string | number>) => string,
): string {
  return `/cycles/${encodeURIComponent(cycle)}/works/${campaignId}${linkSearch({ farm: farmId, cycle })}`;
}

/** Имя работы без префикса кампании «Хлопок-2026: ». */
export function workTitle(name: string): string {
  return name.replace(/^[^:]*:\s*/, '');
}

/* --------------------------------------------------------------- земля */

export interface LandHeadline {
  totalHa: number | null;
  arableHa: number | null;
  irrigatedHa: number | null;
  rainfedHa: number | null;
  agriIrrigatedHa: number | null;
}

const microha = (v: number | null | undefined) => (v === null || v === undefined ? null : v / 1_000_000);

export function landHeadline(balance: ReadonlyArray<Schemas['CategoryAreaOut']>): LandHeadline {
  const by = new Map(balance.map((b) => [b.code, b]));
  const get = (code: string) => microha(by.get(code)?.area_microha);
  return {
    totalHa: get('TOTAL_LAND'),
    arableHa: get('ARABLE'),
    irrigatedHa: get('ARABLE_IRRIGATED'),
    rainfedHa: get('ARABLE_RAINFED'),
    agriIrrigatedHa: get('AGRICULTURAL_LAND_IRRIGATED'),
  };
}

export interface DirectionSeason {
  year: number;
  items: { code: string | null; label: string; ha: number | null; contours: number; phase: string; demo: boolean }[];
  more: number;
}

/** Направления по годам цикла: до трёх главных культур года по площади. */
export function directionsBySeason(dirs: ReadonlyArray<Schemas['FarmDirectionOut']>, perSeason = 3): DirectionSeason[] {
  const byYear = new Map<number, Schemas['FarmDirectionOut'][]>();
  for (const d of dirs) byYear.set(d.cycle_year, [...(byYear.get(d.cycle_year) ?? []), d]);
  return [...byYear.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([year, rows]) => {
      const sorted = [...rows].sort((a, b) => (b.sown_microha ?? 0) - (a.sown_microha ?? 0));
      return {
        year,
        items: sorted.slice(0, perSeason).map((d) => ({
          code: d.crop_code,
          label: cropLabel(d.crop_code).known ? cropLabel(d.crop_code).label : d.crop_name ?? d.crop_code ?? tr(m.noCropName),
          ha: microha(d.sown_microha),
          contours: d.contours,
          phase: d.phase,
          demo: d.is_demo,
        })),
        more: Math.max(0, sorted.length - perSeason),
      };
    });
}

/** Культура контура в году цикла: главная фаза (хлопок/озимые) раньше второстепенной. */
export function cropOfYear(row: Pick<FarmContourRow, 'crops'>, year: number): string | null {
  const marks = row.crops.filter((c) => c.cycle_year === year);
  if (!marks.length) return null;
  const order = ['cotton', 'winter_grain', 'secondary'];
  marks.sort((a, b) => order.indexOf(a.phase) - order.indexOf(b.phase));
  return marks
    .map((m) => (cropLabel(m.crop_code).known ? cropLabel(m.crop_code).label : m.crop_name ?? m.crop_code ?? '—'))
    .filter((v, i, a) => a.indexOf(v) === i)
    .join(' + ');
}

/* ---------------------------------------------------------------- карта */

export function quarantineByContour(rows: ReadonlyArray<FarmContourRow>): Record<string, QuarantineStatusValue> {
  const out: Record<string, QuarantineStatusValue> = {};
  for (const r of rows) out[String(r.contour_id)] = r.quarantine_status === 'red' ? 'red' : r.quarantine_status === 'yellow' ? 'watch' : 'green';
  return out;
}

/** Вода контура → слой «Вода»: те же пороги, что у общего адаптера карты. */
export function waterByContour(rows: ReadonlyArray<FarmContourRow>): Record<string, WaterStateValue> | undefined {
  const withWater = rows.filter((r) => r.water_lines > 0);
  if (!withWater.length) return undefined;
  return waterByAllocations(
    withWater.map((r) => ({
      contour_id: r.contour_id,
      requested_volume_m3: r.water_unmeasured_lines > 0 ? null : r.water_requested_m3 ?? null,
      // Позиции есть, распределений нет — подано ноль, а не «не измерено».
      volume_m3: r.water_delivered_m3 ?? '0',
    })),
  );
}

const CROP_TONES: Record<string, ValuesLayer['legend'][number]['tone']> = {
  COTTON: 'ramp-4',
  GRAIN: 'ramp-2',
};

/**
 * Культуры прошлого года цикла — произвольным слоем: слой «Культуры» общей
 * карты красит текущее размещение реестра, а прошлый сезон приходит из
 * занятостей кабинета. Тоны — ступени, не hex (правило единой карты).
 */
export function cropSeasonLayer(rows: ReadonlyArray<FarmContourRow>, year: number): ValuesLayer {
  const byId: Record<string, string> = {};
  const seen = new Set<string>();
  for (const r of rows) {
    const marks = r.crops.filter((c) => c.cycle_year === year && c.phase !== 'secondary');
    const main = marks[0] ?? r.crops.find((c) => c.cycle_year === year);
    if (!main) continue;
    const key = main.crop_code && CROP_TONES[main.crop_code] ? main.crop_code : 'OTHER';
    byId[String(r.contour_id)] = key;
    seen.add(key);
  }
  const legend = [
    { key: 'COTTON', label: cropName('COTTON'), tone: CROP_TONES.COTTON! },
    { key: 'GRAIN', label: cropName('GRAIN'), tone: CROP_TONES.GRAIN! },
    { key: 'OTHER', label: cropName('OTHER_CROPS'), tone: 'ramp-1' as const },
  ].filter((e) => seen.has(e.key));
  return { title: tr(m.seasonLayerTitle, { year }), byId, legend, noneLabel: tr(m.seasonLayerNone) };
}

/* ----------------------------------------------------- факты приоритета */

export function farmFacts(input: {
  asOf: string;
  today: string;
  cycles: ReadonlyArray<SeasonCycle>;
  breakdowns: ReadonlyArray<FarmBreakdown | null>;
  quarantine: FarmQuarantine | null;
  metrics: Record<string, MetricValue>;
}): FarmFacts {
  const active = input.cycles.filter((c) => c.status === 'active');
  const bds = input.breakdowns.filter((b): b is FarmBreakdown => b !== null);
  const measured = bds.length === active.length && active.length > 0;
  const sum = (pick: (b: FarmBreakdown) => number | null): number | null => {
    if (!measured) return active.length === 0 ? 0 : null;
    let total = 0;
    for (const b of bds) {
      const v = pick(b);
      if (v === null) return null;
      total += v;
    }
    return total;
  };
  const overdueFromCycles = active.reduce<number | null>((s, c) => (s === null || c.overdue.value === null ? null : s + c.overdue.value), 0);
  return {
    asOf: input.asOf,
    today: input.today,
    activeCycles: active.length,
    crops: [...new Set(input.cycles.map((c) => c.crop_code))],
    overdue: overdueFromCycles,
    dueSoon: sum((b) => (b.deadlines ? b.deadlines.due_soon_total : 0)),
    waterGapContours: sum((b) => (b.water ? b.water.works.reduce((s, w) => s + w.gap_contours_total, 0) : 0)),
    waterSharePct: metricNumber(input.metrics, 'water.share_pct'),
    conflicts: sum((b) => (b.conflicts ? b.conflicts.open_total : 0)),
    redContours: input.quarantine ? input.quarantine.now.red ?? 0 : null,
    yellowContours: input.quarantine ? input.quarantine.now.yellow ?? 0 : null,
    requirementsOverdue: input.quarantine ? input.quarantine.requirements.overdue ?? 0 : null,
    harvestPlanT: metricNumber(input.metrics, 'harvest.plan_t'),
    // i18n-ignore: сопоставление с названием этапа из данных, на экран не выводится
    harvestActive: active.some((c) => /уборк/i.test(c.stage?.name ?? '')),
  };
}

export function contourFacts(input: {
  asOf: string;
  today: string;
  quarantineStatus: string | null;
  history: ContourHistory | null;
}): ContourFacts {
  const h = input.history;
  const status = input.quarantineStatus === 'red' ? 'red' : input.quarantineStatus === 'yellow' ? 'yellow' : input.quarantineStatus === 'green' ? 'green' : null;
  const reqs = h?.requirements ?? null;
  return {
    asOf: input.asOf,
    today: input.today,
    quarantineStatus: status,
    requirementsOpen: reqs ? reqs.filter((r) => r.requirement.state === 'open' || r.requirement.state === 'overdue').length : null,
    requirementsOverdue: reqs ? reqs.filter((r) => r.requirement.state === 'overdue').length : null,
    // Только работы идущих агроциклов (и предписания вне цикла) — та же основа,
    // что у блока «Сейчас» и списка «после срока» (agroai-0e8).
    overdueWorks: h ? h.works.filter((w) => w.cycle_running && w.status === 'overdue').length : null,
    inWindowWorks: h ? h.works.filter((w) => w.cycle_running && w.status === 'in_window').length : null,
    waterShort: h
      ? h.water.filter((l) => l.requested_m3 !== null && Number(l.requested_m3) > 0 && Number(l.delivered_m3 ?? 0) < Number(l.requested_m3) * 0.95).length
      : null,
  };
}

/* ------------------------------------------------------------ карантин */

// Словари читаются как `LABEL[code]` (в том числе карточкой контура): перевод
// берётся в момент обращения.
export const REQ_STATE_LABEL: Record<string, string> = messageRecord({
  open: m.reqOpen,
  overdue: m.reqOverdue,
  extended: m.reqExtended,
  eliminated: m.reqEliminated,
  eliminated_late: m.reqEliminatedLate,
});

export const Q_STATUS_LABEL: Record<string, string> = messageRecord({ green: m.qGreen, yellow: m.qYellow, red: m.qRed });

export const Q_DIRECTION_LABEL: Record<string, string> = messageRecord({ pests: m.dirPests, diseases: m.dirDiseases, weeds: m.dirWeeds });

export const FOLLOWUP_LABEL: Record<string, string> = messageRecord({
  eliminated: m.fuEliminated,
  reduced: m.fuReduced,
  unchanged: m.fuUnchanged,
  increased: m.fuIncreased,
});

export const CONTOUR_WORK_STATUS_LABEL: Record<string, string> = messageRecord({
  reported: m.cwReported,
  in_progress: m.cwInProgress,
  overdue: m.cwOverdue,
  in_window: m.cwInWindow,
  awaiting_window: m.cwAhead,
});

/** Сезоны работ контура: последние сверху; кампании вне цикла — отдельной группой. */
export function worksBySeason(works: ReadonlyArray<ContourWork>): { key: string; label: string; cycle: string | null; rows: ContourWork[] }[] {
  const groups = new Map<string, { key: string; label: string; cycle: string | null; rows: ContourWork[] }>();
  for (const w of works) {
    const key = w.cycle ?? `other:${w.season_label ?? ''}`;
    const label = w.cycle
      ? cycleName({ crop_code: w.crop_code ?? '', season_label: w.season_label ?? '' })
      : w.season_label
        ? tr(m.outsideSeason, { season: w.season_label })
        : tr(m.outside);
    const g = groups.get(key) ?? { key, label, cycle: w.cycle, rows: [] };
    g.rows.push(w);
    groups.set(key, g);
  }
  return [...groups.values()].sort((a, b) => {
    if (!!a.cycle !== !!b.cycle) return a.cycle ? -1 : 1;
    return (b.rows[0]?.season_label ?? '').localeCompare(a.rows[0]?.season_label ?? '');
  });
}
